/**
 * The onboarding wizard's frame (phase 7b item 3): the four steps with their
 * state (done, skipped, to do), the one open now, and "Close" / "Finish".
 * The open step's panel is the container's (`panel`, one of
 * `OnboardingSteps.tsx`); so is which step is open and what "skip" means.
 */
import { CheckCircle2Icon, CircleDashedIcon, CircleIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { OnboardingStepIdWire } from '@norna/api-contract/onboarding.js';
import { STEP_TITLES, type WizardStep } from '../../lib/onboarding.js';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';

export type OnboardingWizardProps = {
  readonly steps: readonly WizardStep[];
  /** The step shown; null: every step is done or skipped (the finish panel). */
  readonly open: OnboardingStepIdWire | null;
  readonly panel: ReactNode;
  /** False where progress is not saved (a node): said, so a skip is not expected to be remembered. */
  readonly persisted: boolean;
  readonly onOpen: (step: OnboardingStepIdWire) => void;
  /** On to the next step still to do (or the finish, when none is left): offered once the open step is done or skipped. */
  readonly onNext: () => void;
  readonly onSkip: (step: OnboardingStepIdWire) => void;
  readonly onClose: () => void;
  readonly onFinish: () => void;
};

function StepIcon({ status }: { readonly status: WizardStep['status'] }) {
  if (status === 'done') return <CheckCircle2Icon className="size-4 text-primary" aria-hidden="true" />;
  if (status === 'skipped') return <CircleDashedIcon className="size-4 text-muted-foreground" aria-hidden="true" />;
  return <CircleIcon className="size-4 text-muted-foreground" aria-hidden="true" />;
}

const STATUS_WORD: Readonly<Record<WizardStep['status'], string>> = { done: 'done', skipped: 'skipped', todo: 'to do' };

export function OnboardingWizard({ steps, open, panel, persisted, onOpen, onNext, onSkip, onClose, onFinish }: OnboardingWizardProps) {
  const openStep = steps.find((s) => s.id === open) ?? null;
  return (
    <Card className="mx-auto w-full max-w-4xl" data-testid="onboarding-wizard">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Set up your studio</CardTitle>
          <CardDescription>
            Four steps to a first show: a show style, a studio, a server (or the cloud), a rundown. Every step can be skipped and done later.
            {persisted ? null : ' This server does not save your progress.'}
          </CardDescription>
        </div>
        <Button variant="outline" onClick={onClose} data-testid="onboarding-close">
          Close
        </Button>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-4">
        <ol className="grid content-start gap-1 md:col-span-1" aria-label="Steps">
          {steps.map((step, index) => (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onOpen(step.id)}
                aria-current={step.id === open ? 'step' : undefined}
                data-testid={`onboarding-step-${step.id}`}
                data-status={step.status}
                className={cn('flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent', step.id === open && 'bg-accent font-medium')}
              >
                <StepIcon status={step.status} />
                <span className="flex-1">
                  {index + 1}. {STEP_TITLES[step.id]}
                </span>
                <span className="text-xs text-muted-foreground">{STATUS_WORD[step.status]}</span>
              </button>
            </li>
          ))}
        </ol>
        <section className="grid content-start gap-4 md:col-span-3" aria-label={openStep === null ? 'Finished' : STEP_TITLES[openStep.id]}>
          {openStep === null ? (
            <div className="grid gap-3" data-testid="onboarding-finished">
              <p className="text-sm">You are set up. Open your rundown to start editing, or come back here any time from “Setup”.</p>
              <Button className="justify-self-start" onClick={onFinish} data-testid="onboarding-finish">
                Finish
              </Button>
            </div>
          ) : (
            <>
              {panel}
              {openStep.status === 'todo' ? (
                <Button variant="ghost" className="justify-self-start" onClick={() => onSkip(openStep.id)} data-testid="onboarding-skip">
                  Skip this step
                </Button>
              ) : (
                <Button className="justify-self-start" onClick={onNext} data-testid="onboarding-next">
                  Next
                </Button>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
